import type { ReactNode } from 'react';

/**
 * Nothing to show, said as what it is — design-system plan DS-6a (5.1, 5.6; F501, F79, F122, F149, F180, F245, F606).
 *
 * Three kinds, because "no invoices yet" and "nothing matches these filters" are different news:
 * - `first-run` (the default): `No {things} yet.`, one sentence on how they appear, and the one action that makes
 *   the first one;
 * - `filtered`: `Nothing matches these filters.` and a way to clear them;
 * - `inline`: one muted line inside a small card, where a centred block would be larger than the card.
 * A list that FAILED to load is never shown as empty — that is an `ErrorState` (F70).
 *
 * `hint` is the sentence (`body` is its old name, kept for the callers that pass it); `headingLevel` makes the title
 * a real heading where the empty state stands in for a section.
 */
export type EmptyVariant = 'first-run' | 'filtered' | 'inline';

export function EmptyState({
  title,
  hint,
  body,
  action,
  variant = 'first-run',
  headingLevel,
}: {
  title: string;
  hint?: string;
  /** The old name of `hint`. */
  body?: string;
  action?: ReactNode;
  variant?: EmptyVariant;
  headingLevel?: 2 | 3 | 4;
}) {
  const sentence = hint ?? body;
  if (variant === 'inline') {
    return (
      <p className="wb-empty wb-empty--inline">
        {title}
        {sentence ? ` ${sentence}` : ''}
      </p>
    );
  }
  const Heading = headingLevel ? (`h${headingLevel}` as const) : 'div';
  return (
    <div className={`wb-empty wb-empty--${variant}`}>
      <Heading className="wb-empty__title">{title}</Heading>
      {sentence ? <div className="wb-empty__body">{sentence}</div> : null}
      {action ? <div className="wb-empty__action">{action}</div> : null}
    </div>
  );
}
